import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import RefreshIcon from '@mui/icons-material/Refresh'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import API from '../../../services/API'
import { TenantSettingsContext } from '../../../App'
import { vatMultiplierForItem } from '../../../helpers/region'

const STATUS_COLORS = {
  Draft: 'default',
  Sent: 'info',
  Accepted: 'success',
  Declined: 'error',
  Converted: 'success',
  Superseded: 'default',
  Expired: 'warning',
  Converted: 'success',
}

const getQuoteTotal = (quote, region) =>
  (quote?.lines || []).reduce(
    (total, line) =>
      total +
      (Number(line.quantity) || 0) *
        (Number(line.unitPrice) || 0) *
        vatMultiplierForItem(line.vat, region),
    0,
  )

const QuoteEditorDialog = ({
  quote,
  currency,
  journeys,
  onClose,
  onChanged,
  onQuoteRefreshed,
  defaultMileageOpen = false,
}) => {
  const [lines, setLines] = useState([])
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sendOpen, setSendOpen] = useState(false)
  const [sendEmails, setSendEmails] = useState('')
  const [acceptOpen, setAcceptOpen] = useState(false)
  const [acceptName, setAcceptName] = useState('')
  const [acceptChannel, setAcceptChannel] = useState('Phone')
  const [declineOpen, setDeclineOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [tariffs, setTariffs] = useState([])
  const [selectedTariffId, setSelectedTariffId] = useState('')
  const [mileageOpen, setMileageOpen] = useState(false)
  // Decisions saved during this dialog session - journeys prop won't refresh
  // until the booking reloads, so track saves locally to drive the collapse
  const [mileageDecisions, setMileageDecisions] = useState({})
  const [mileageWarnings, setMileageWarnings] = useState([])

  const { tenantInfo } = useContext(TenantSettingsContext)
  const region = tenantInfo?.region

  const isDraft = quote?.status === 'Draft'
  const canAccept = ['Draft', 'Sent'].includes(quote?.status)

  useEffect(() => {
    if (!quote) return
    setLines((quote.lines || []).map((line) => ({ ...line })))
    setNotes(quote.notes || '')
    setSelectedTariffId(String(quote.tariff?._id || quote.tariff || ''))
    setError('')
    setSendOpen(false)
    setAcceptOpen(false)
    setDeclineOpen(false)
    setMileageDecisions({})
    setMileageWarnings([])
    // Open the panel for a freshly generated quote, or while a journey
    // still needs a mileage decision
    setMileageOpen(
      defaultMileageOpen ||
        (journeys ?? []).some((journey) => !journey.billedMileage?.mode),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?._id])

  // Tariff options for re-pricing draft quotes against a different tariff
  useEffect(() => {
    if (!quote || quote.status !== 'Draft' || tariffs.length) return
    API.get('/pts/tariffs?activeOnly=true')
      .then((res) => {
        if (res.status === 200) setTariffs(res.data || [])
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote])

  if (!quote) return null

  const setLine = (index, field, value) =>
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)),
    )

  const total = getQuoteTotal({ lines }, region)

  // The lines were generated with the quote's stored tariff - selecting a
  // different one only takes effect on re-price, so flag the gap
  const quoteTariffId = String(quote.tariff?._id || quote.tariff || '')
  const selectedTariff = tariffs.find(
    (tariff) => String(tariff._id) === selectedTariffId,
  )
  const tariffMismatch =
    isDraft &&
    !!selectedTariffId &&
    !!quoteTariffId &&
    selectedTariffId !== quoteTariffId

  // API.js never rejects on 4xx/5xx (validateStatus: () => true), so every
  // response has to be checked - callers that relied on axios throwing used
  // to report success on failure (Save Draft, Send, Accept, Decline)
  const ensureOk = (res) => {
    if (res.status < 200 || res.status >= 300) {
      throw new Error(
        res?.data?.error || 'Something went wrong. Please try again.',
      )
    }
    return res
  }

  const call = async (fn, closeAfter = false) => {
    setBusy(true)
    setError('')
    try {
      await fn()
      onChanged?.()
      if (closeAfter) onClose?.()
    } catch (e) {
      setError(
        e?.message ||
          'Something went wrong. Please try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  const saveDraft = () =>
    call(async () => ensureOk(await API.put(`/pts/quotes/${quote._id}`, { lines, notes })))

  const reprice = () =>
    call(async () => {
      const res = ensureOk(
        await API.post(`/pts/quotes/${quote._id}/reprice`, {
          tariffID: selectedTariffId || undefined,
        }),
      )
      setLines((res.data.lines || []).map((line) => ({ ...line })))
      // Push the updated quote (new tariff + tariffName) into the parent
      // immediately so the header chip reflects the change without
      // waiting for the list refresh
      onQuoteRefreshed?.(res.data)
    })

  // A journey's mileage billing changed: refresh just the perMile lines so
  // the quote (and total) update immediately without wiping manual edits.
  // The current edits ride along in the body and are persisted first.
  // mode is null for base-location-only saves - pricing may still have
  // changed (base-to-base distance) but it isn't a billing decision, so it
  // doesn't count towards collapsing the panel
  const handleMileageSaved = (journeyId, mode) =>
    call(async () => {
      const res = await API.post(`/pts/quotes/${quote._id}/reprice-mileage`, {
        lines,
        notes,
      })
      if (res.status !== 200) {
        throw new Error(
          res.data?.error || 'Unable to update the mileage line.',
        )
      }
      setLines((res.data.lines || []).map((line) => ({ ...line })))
      onQuoteRefreshed?.(res.data)

      const warnings = res.data.mileageWarnings || []
      setMileageWarnings(warnings)
      if (warnings.length) {
        // Something stopped the line appearing - keep the panel open with
        // the explanation rather than collapsing over it
        setMileageOpen(true)
        return
      }
      if (mode) {
        const nextDecisions = { ...mileageDecisions, [journeyId]: mode }
        setMileageDecisions(nextDecisions)
        if (
          (journeys ?? []).every(
            (journey) =>
              nextDecisions[journey._id] || journey.billedMileage?.mode,
          )
        ) {
          setMileageOpen(false)
        }
      }
    })

  const send = () =>
    call(async () => {
      // Persist any line edits before sending (Drafts only - a resend of a
      // Sent quote has nothing editable to persist)
      if (quote.status === 'Draft') {
        ensureOk(await API.put(`/pts/quotes/${quote._id}`, { lines, notes }))
      }
      const emails = sendEmails
        .split(/[,;\s]+/)
        .map((email) => email.trim())
        .filter(Boolean)
      const res = ensureOk(
        await API.post(`/pts/quotes/${quote._id}/send`, { emails }),
      )
      setSendOpen(false)
      if (res.data?.emailWarnings?.length) {
        // Sent, but not everyone received it - keep the dialog open on the
        // warning rather than closing as if all was well
        throw new Error(res.data.emailWarnings.join(' '))
      }
    }, true)

  const accept = () =>
    call(
      async () =>
        ensureOk(
          await API.post(`/pts/quotes/${quote._id}/accept`, {
            acceptedByName: acceptName,
            channel: acceptChannel,
          }),
        ),
      true,
    )

  const decline = () =>
    call(
      async () =>
        ensureOk(
          await API.post(`/pts/quotes/${quote._id}/decline`, {
            reason: declineReason,
          }),
        ),
      true,
    )

  const revise = () =>
    call(async () => {
      const res = ensureOk(await API.post(`/pts/quotes/${quote._id}/revise`))
      onQuoteRefreshed?.(res.data)
    }, true)

  const convert = () =>
    call(
      async () => ensureOk(await API.post(`/pts/quotes/${quote._id}/convert`)),
      true,
    )

  return (
    <Dialog
      open={!!quote}
      onClose={busy ? null : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          width="100%"
          justifyContent="space-between"
        >
          {quote.quoteRef}
          <Chip
            size="small"
            label={quote.status}
            color={STATUS_COLORS[quote.status] || 'default'}
            sx={{ borderRadius: 1, fontWeight: 700 }}
          />
          {(selectedTariff?.name || quote.tariffName) && (
            <Tooltip
              title={
                tariffMismatch
                  ? `Lines are still priced with "${quote.tariffName}" - use Re-price from tariff to apply this tariff`
                  : ''
              }
            >
              <Chip
                size="small"
                variant="outlined"
                color={tariffMismatch ? 'warning' : 'default'}
                label={`Tariff: ${selectedTariff?.name ?? quote.tariffName}`}
                sx={{ borderRadius: 1 }}
              />
            </Tooltip>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {quote.status === 'Sent' && quote.validUntil && (
            <Alert severity="info">
              Sent {moment(quote.sentAt).format('Do MMM')}
              {quote.sentTo?.length ? ` to ${quote.sentTo.join(', ')}` : ''} —
              valid until {moment(quote.validUntil).format('Do MMM YYYY')}.
              Site/admin portal users can accept it online.
            </Alert>
          )}
          {quote.status === 'Accepted' && (
            <Alert severity="success">
              Accepted {moment(quote.acceptedAt).format('Do MMM YYYY HH:mm')}
              {quote.acceptedBy?.name ? ` by ${quote.acceptedBy.name}` : ''}
              {quote.acceptanceChannel ? ` via ${quote.acceptanceChannel}` : ''}
              . The booking has moved into the planning pool.
            </Alert>
          )}

          {isDraft && (journeys ?? []).length > 0 && (
            <Stack
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'anBackground.main',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => setMileageOpen((prev) => !prev)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="subtitle2" fontWeight={800}>
                  Mileage
                </Typography>
                <IconButton
                  size="small"
                  aria-label={mileageOpen ? 'Hide mileage' : 'Show mileage'}
                  sx={{
                    transform: mileageOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 150ms ease',
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Collapse in={mileageOpen}>
                <Stack gap={1.5} pt={1.5}>
                  {mileageWarnings.map((warning) => (
                    <Alert key={warning} severity="warning" sx={{ py: 0 }}>
                      {warning}
                    </Alert>
                  ))}
                  <Stack gap={1.5} divider={<Divider flexItem />}>
                    {journeys.map((journey) => (
                      <MileageJourneyRow
                        key={journey._id}
                        journey={journey}
                        onSaved={(mode) =>
                          handleMileageSaved(journey._id, mode)
                        }
                      />
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Saving updates the quote&apos;s mileage line and total
                    automatically.
                  </Typography>
                </Stack>
              </Collapse>
            </Stack>
          )}

          <Stack spacing={1}>
            {lines.map((line, index) => {
              // Mileage lines are engine-generated from the journey's
              // mileage billing - everything but VAT is locked here so the
              // figure can only change via the Mileage Billing panel
              const isMileageLine = line.ruleKind === 'perMile'
              return (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <TextField
                    label="Description"
                    size="small"
                    fullWidth
                    multiline
                    maxRows={4}
                    value={line.description}
                    onChange={(e) =>
                      setLine(index, 'description', e.target.value)
                    }
                    helperText={line.detail || undefined}
                    disabled={!isDraft || isMileageLine}
                  />
                  <TextField
                    label="Qty"
                    type="number"
                    size="small"
                    value={line.quantity}
                    onChange={(e) => setLine(index, 'quantity', e.target.value)}
                    sx={{ width: 80, flexShrink: 0 }}
                    disabled={!isDraft || isMileageLine}
                  />
                  <TextField
                    label={`Unit (${currency})`}
                    type="number"
                    size="small"
                    value={line.unitPrice}
                    onChange={(e) =>
                      setLine(index, 'unitPrice', e.target.value)
                    }
                    sx={{ width: 110, flexShrink: 0 }}
                    disabled={!isDraft || isMileageLine}
                  />
                  <TextField
                    select
                    label="VAT"
                    size="small"
                    value={line.vat || 'none'}
                    onChange={(e) => setLine(index, 'vat', e.target.value)}
                    sx={{ width: 130, flexShrink: 0 }}
                    disabled={!isDraft}
                  >
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="none">None</MenuItem>
                  </TextField>
                  {isDraft &&
                    (isMileageLine ? (
                      <Tooltip title="Generated from this journey's mileage settings — change it in the Mileage panel above. Only VAT is editable here.">
                        <LockOutlinedIcon
                          fontSize="small"
                          sx={{ mt: 1, color: 'text.disabled' }}
                        />
                      </Tooltip>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() =>
                          setLines((prev) =>
                            prev.filter((l, i) => i !== index),
                          )
                        }
                        sx={{ mt: 0.5 }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    ))}
                </Stack>
              )
            })}
            {isDraft && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setLines((prev) => [
                      ...prev,
                      {
                        description: '',
                        quantity: 1,
                        unitPrice: 0,
                        vat: 'standard',
                      },
                    ])
                  }
                >
                  Add line
                </Button>
                {tariffs.length > 0 && (
                  <TextField
                    select
                    size="small"
                    label="Tariff"
                    value={
                      tariffs.some((t) => String(t._id) === selectedTariffId)
                        ? selectedTariffId
                        : ''
                    }
                    onChange={(e) => setSelectedTariffId(e.target.value)}
                    sx={{ minWidth: 200 }}
                  >
                    {tariffs.map((tariff) => (
                      <MenuItem key={tariff._id} value={String(tariff._id)}>
                        {tariff.name}
                        {tariff.isDefault ? ' (default)' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <Tooltip title="Regenerate all lines from the selected tariff (replaces manual edits)">
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={reprice}
                  >
                    Re-price from tariff
                  </Button>
                </Tooltip>
              </Stack>
            )}
            {tariffMismatch && (
              <Alert severity="warning" sx={{ py: 0 }}>
                These lines were priced with &quot;{quote.tariffName}&quot; —
                use Re-price from tariff to apply &quot;
                {selectedTariff?.name ?? 'the selected tariff'}&quot;.
              </Alert>
            )}
          </Stack>

          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <TextField
              label="Notes"
              size="small"
              multiline
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!isDraft}
              sx={{ flex: 1, mr: 2 }}
            />
            <Typography
              sx={{ fontWeight: 900, fontSize: '1.1rem', alignSelf: 'center' }}
            >
              Total: {currency}
              {total.toFixed(2)}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        <Button onClick={onClose} color="inherit" disabled={busy}>
          Close
        </Button>
        {isDraft && (
          <Button onClick={saveDraft} disabled={busy}>
            Save Draft
          </Button>
        )}
        {/* A sent/accepted/expired quote can't be edited - Revise clones it
            back to a fresh draft and supersedes this one (revising or
            declining an Accepted quote needs the Finance team) */}
        {['Sent', 'Accepted', 'Expired'].includes(quote?.status) &&
          !quote?.convertedInvoice && (
            <Button disabled={busy} onClick={revise}>
              Revise
            </Button>
          )}
        {quote?.status === 'Accepted' && !quote?.convertedInvoice && (
          <>
            <Button
              color="error"
              disabled={busy}
              onClick={() => setDeclineOpen(true)}
            >
              Decline
            </Button>
            <Button variant="contained" disabled={busy} onClick={convert}>
              Convert to Invoice
            </Button>
          </>
        )}
        {canAccept && (
          <>
            <Button
              color="error"
              disabled={busy}
              onClick={() => setDeclineOpen(true)}
            >
              Decline
            </Button>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => setAcceptOpen(true)}
            >
              Mark Accepted
            </Button>
          </>
        )}
        {isDraft && (
          <Button
            variant="contained"
            disabled={busy || !lines.length}
            onClick={() => setSendOpen(true)}
          >
            Send Quote
          </Button>
        )}
      </DialogActions>

      <Dialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Send quote {quote.quoteRef}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} pt={1}>
            <TextField
              label="Email addresses"
              helperText="Separate multiple addresses with commas. Leave blank to mark as sent without emailing."
              fullWidth
              value={sendEmails}
              onChange={(e) => setSendEmails(e.target.value)}
            />
            <Typography variant="caption" color="text.secondary">
              Sending locks the lines and starts the validity period. Site and
              admin portal users will also see the quote in the hospital portal.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={send} disabled={busy}>
            {busy ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Mark quote as accepted</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} pt={1}>
            <TextField
              label="Accepted by (name)"
              fullWidth
              value={acceptName}
              onChange={(e) => setAcceptName(e.target.value)}
            />
            <TextField
              select
              label="Channel"
              fullWidth
              value={acceptChannel}
              onChange={(e) => setAcceptChannel(e.target.value)}
            >
              {['Phone', 'Email', 'Internal'].map((channel) => (
                <MenuItem key={channel} value={channel}>
                  {channel}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              Accepting moves the booking into the planning pool.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={accept} disabled={busy}>
            {busy ? 'Saving...' : 'Confirm Acceptance'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Decline quote</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason"
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={decline}
            disabled={busy}
          >
            {busy ? 'Saving...' : 'Decline Quote'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

// Per-journey mileage billing: GPS/route tracked (provisional on quotes,
// finalised at invoice) or a manually-set static figure. The set flow
// offers route-based suggestions from the mapping service.
const MileageJourneyRow = ({ journey, onChanged, onSaved }) => {
  const existing = journey.billedMileage
  const [mode, setMode] = useState(existing?.mode ?? 'tracked')
  const [miles, setMiles] = useState(
    existing?.mode === 'set' && Number.isFinite(existing?.miles)
      ? String(existing.miles)
      : '',
  )
  const [baseLocationId, setBaseLocationId] = useState(
    String(journey.baseLocation?._id || journey.baseLocation || ''),
  )
  const [baseOptions, setBaseOptions] = useState(null)
  const [suggestion, setSuggestion] = useState(null)
  const [loadingSuggestion, setLoadingSuggestion] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Base locations for the picker - needed when the booking was created
  // without one (base-to-base mileage can't resolve until it's set)
  useEffect(() => {
    API.get('/base-location-options')
      .then((res) => {
        if (res.status === 200) setBaseOptions(res.data || [])
      })
      .catch(() => setBaseOptions([]))
  }, [])

  const loadSuggestion = () => {
    setLoadingSuggestion(true)
    API.get(`/pts/journeys/${journey._id}/mileage-suggestion`)
      .then((res) => {
        if (res.status === 200) setSuggestion(res.data)
      })
      .catch(() => {})
      .finally(() => setLoadingSuggestion(false))
  }

  useEffect(() => {
    if (mode !== 'set' || loadingSuggestion) return
    loadSuggestion()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  // The suggestion endpoint resolves base-to-base from the journey's saved
  // base, so the base has to persist as soon as it's picked — in "set"
  // mode the main Save button stays disabled until a mileage is entered,
  // which the base-to-base suggestion is needed to provide.
  const persistBase = async (newBaseId) => {
    if (!newBaseId) return
    try {
      const res = await API.put(`/pts/journeys/${journey._id}/billed-mileage`, {
        baseLocation: newBaseId,
      })
      if (res.status === 200) {
        onChanged?.()
        // Base moves change base-to-base pricing even without a new mode
        onSaved?.(null)
        if (mode === 'set') {
          setSuggestion(null)
          loadSuggestion()
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  const save = async () => {
    setSaving(true)
    setSaved(false)
    try {
      const res = await API.put(`/pts/journeys/${journey._id}/billed-mileage`, {
        mode,
        miles: mode === 'set' ? Number(miles) : undefined,
        baseLocation: baseLocationId || undefined,
      })
      if (res.status === 200) {
        setSaved(true)
        onChanged?.()
        onSaved?.(mode)
        // Base may have just been set/changed - refresh the suggestions
        if (mode === 'set') {
          setSuggestion(null)
          loadSuggestion()
        }
      }
    } catch (e) {
      console.log(e)
    } finally {
      setSaving(false)
    }
  }

  const journeyLabel = `${journey.journeyType || 'Journey'}${
    journey?.pickup?.name || journey?.dropoff?.name
      ? ` — ${[journey?.pickup?.name, journey?.dropoff?.name]
          .filter(Boolean)
          .join(' → ')}`
      : ''
  }`

  const canSave =
    mode !== 'set' || (Number.isFinite(Number(miles)) && Number(miles) > 0)

  return (
    <Stack gap={2}>
      <Typography variant="body2" fontWeight={700}>
        {journeyLabel}
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={1}
        alignItems={{ sm: 'center' }}
        justifyContent='space-between'
        width='100%'
      >
        <TextField
          select
          size="small"
          label="Mileage"
          value={mode}
          onChange={(e) => {
            setMode(e.target.value)
            setSaved(false)
          }}
          sx={{ minWidth: 220, flex: 1 }}
        >
          <MenuItem value="tracked">Tracked — provisional on quote</MenuItem>
          <MenuItem value="set">Set mileage — static</MenuItem>
          <MenuItem value="none">No mileage charge</MenuItem>
        </TextField>
        {mode === 'set' && (
          <TextField
            size="small"
            label="Miles"
            type="number"
            value={miles}
            onChange={(e) => {
              setMiles(e.target.value)
              setSaved(false)
            }}
            inputProps={{ min: 0, step: 0.1 }}
            sx={{ width: 120 }}
          />
        )}
        {(baseOptions ?? []).length > 0 &&
          (baseOptions.length > 1 || !baseLocationId) && (
            <TextField
              select
              size="small"
              label="Billing base"
              value={
                baseOptions.some((b) => String(b._id) === baseLocationId)
                  ? baseLocationId
                  : ''
              }
              onChange={(e) => {
                setBaseLocationId(e.target.value)
                setSaved(false)
                persistBase(e.target.value)
              }}
              error={!baseLocationId}
              sx={{ minWidth: 180 }}
            >
              {baseOptions.map((base) => (
                <MenuItem key={base._id} value={String(base._id)}>
                  {base.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        <Button
          size="small"
          variant="contained"
          onClick={save}
          disabled={saving || !canSave}
        >
          {saving ? 'Saving...' : saved ? 'Saved' : 'Save'}
        </Button>
      </Stack>
      {!baseLocationId && (
        <Typography variant="caption" color="warning.main">
          No billing base set for this journey - base-to-base mileage can&apos;t
          be calculated until one is chosen and saved.
        </Typography>
      )}
      {mode === 'set' &&
        (loadingSuggestion ? (
          <Typography variant="caption" color="text.secondary">
            Fetching route-based suggestions...
          </Typography>
        ) : suggestion?.journeyMiles || suggestion?.baseToBaseMiles ? (
          <Stack gap={0.5}>
            <Typography variant="caption" fontWeight={700}>
              Suggested mileage from the mapped route:
            </Typography>
            {suggestion?.journeyMiles && (
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary" flex={1}>
                  Pickup to dropoff
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  {suggestion.journeyMiles} mi
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ py: 0, minWidth: 48 }}
                  onClick={() => {
                    setMiles(String(suggestion.journeyMiles))
                    setSaved(false)
                  }}
                >
                  Use
                </Button>
              </Stack>
            )}
            {suggestion?.baseToBaseMiles && (
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary" flex={1}>
                  Base to base (base → patient pickup → dropoff → base)
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  {suggestion.baseToBaseMiles} mi
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ py: 0, minWidth: 48 }}
                  onClick={() => {
                    setMiles(String(suggestion.baseToBaseMiles))
                    setSaved(false)
                  }}
                >
                  Use
                </Button>
              </Stack>
            )}
            {!suggestion?.baseToBaseMiles && baseLocationId && (
              <Typography variant="caption" color="text.secondary">
                {suggestion?.baseToBaseSource === 'noBaseCoordinates'
                  ? 'Base-to-base mileage unavailable — the selected billing base has no map location. Add one in Site Configuration → Base Locations.'
                  : 'Base-to-base mileage could not be calculated for this journey.'}
              </Typography>
            )}
          </Stack>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No route suggestion available (check the journey addresses, patient
            address and base location have map points).
          </Typography>
        ))}
      {mode === 'tracked' && (
        <Typography variant="caption" color="text.secondary">
          Quotes show this mileage as a provisional charge; the billed figure is
          finalised when the journey is invoiced.
        </Typography>
      )}
      {mode === 'none' && (
        <Typography variant="caption" color="text.secondary">
          No mileage is charged for this journey, even if the tariff has a
          mileage rate.
        </Typography>
      )}
    </Stack>
  )
}

const BookingQuotes = ({ booking }) => {
  const { orgConfig, tenantInfo } = useContext(TenantSettingsContext)
  const { orgSettings } = orgConfig
  const region = tenantInfo?.region
  const currency = orgSettings?.currency ?? '£'
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState('')
  const [openQuote, setOpenQuote] = useState(null)
  // The quote just generated this session - its dialog opens with the
  // Mileage panel expanded so the mileage decision isn't missed
  const [generatedQuoteId, setGeneratedQuoteId] = useState(null)

  const loadQuotes = () => {
    if (!booking?._id) return
    setLoading(true)
    API.get(`/pts/bookings/${booking._id}/quotes`)
      .then((res) => {
        if (res.status !== 200) {
          setError(res?.data?.error || 'Unable to load quotes for this booking.')
          return
        }
        const nextQuotes = res.data || []
        setQuotes(nextQuotes)
        setOpenQuote((current) =>
          current
            ? nextQuotes.find((quote) => quote._id === current._id) || null
            : current,
        )
      })
      .catch(() => setError('Unable to load quotes for this booking.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadQuotes()
  }, [booking?._id])

  const generateQuote = async () => {
    setGenerating(true)
    setError('')
    try {
      const res = await API.post(`/pts/bookings/${booking._id}/quotes`)
      if (res.status === 201) {
        setGeneratedQuoteId(res.data._id)
        setOpenQuote(res.data)
        loadQuotes()
      } else {
        setError(res.data?.error || 'Unable to generate a quote.')
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Unable to generate a quote.')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          Auto-priced quotes for this booking. Accepting a quote schedules the
          booking for planning.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<RequestQuoteIcon />}
          onClick={generateQuote}
          disabled={generating}
        >
          {generating ? 'Pricing...' : 'Generate Quote'}
        </Button>
      </Stack>
      <CustomDataGrid
        rows={quotes}
        loading={loading}
        getRowId={(row) => row._id}
        autoHeight
        disableRowSelectionOnClick
        onRowClick={({ row }) => setOpenQuote(row)}
        localeText={{ noRowsLabel: 'No quotes for this booking' }}
        slots={{ toolbar: () => null, footer: () => null }}
        columns={[
          { field: 'quoteRef', headerName: 'Quote', minWidth: 110 },
          {
            field: 'status',
            headerName: 'Status',
            minWidth: 120,
            renderCell: ({ row }) => (
              <Chip
                size="small"
                label={row.status}
                color={STATUS_COLORS[row.status] || 'default'}
                sx={{ borderRadius: 1, fontWeight: 700 }}
              />
            ),
          },
          {
            field: 'tariffName',
            headerName: 'Tariff',
            flex: 1,
            minWidth: 130,
          },
          {
            field: 'total',
            headerName: 'Total',
            minWidth: 110,
            renderCell: ({ row }) =>
              `${currency}${getQuoteTotal(row, region).toFixed(2)}`,
          },
          {
            field: 'validUntil',
            headerName: 'Valid Until',
            minWidth: 120,
            renderCell: ({ row }) =>
              row.validUntil
                ? moment(row.validUntil).format('DD/MM/YYYY')
                : '—',
          },
          {
            field: 'createdAt',
            headerName: 'Created',
            minWidth: 120,
            renderCell: ({ row }) =>
              row.createdAt ? moment(row.createdAt).format('DD/MM/YYYY') : '',
          },
        ]}
      />
      <QuoteEditorDialog
        quote={openQuote}
        currency={currency}
        journeys={booking?.journeys}
        onClose={() => setOpenQuote(null)}
        onChanged={loadQuotes}
        onQuoteRefreshed={setOpenQuote}
        defaultMileageOpen={
          !!openQuote && openQuote._id === generatedQuoteId
        }
      />
    </Stack>
  )
}

export default BookingQuotes
